import { useState, useEffect, useRef } from "react";

// ─── SEO HEAD ──────────────────────────────────────────────────────────────────
function SEOHead() {
  useEffect(() => {
    document.title = "IVF Software & IVF Clinic Management System | Hypervolar";
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    setMeta("description", "Hypervolar's IVF Software is a complete IVF clinic management system covering EMR, IVF cycle tracking, CRM, billing, and inventory — purpose-built for fertility clinics. Book a demo today.");
    setMeta("keywords", "IVF software, IVF clinic software, IVF management system, IVF patient management software, IVF cycle tracking software, IVF EMR system, IVF CRM software, IVF billing software, IVF inventory management, fertility clinic software India");
    setMeta("robots", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
    setLink("canonical", "https://www.hypervolar.com/ivf-software");
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.hypervolar.com/ivf-software", true);
    setMeta("og:title", "IVF Software & IVF Clinic Management System | Hypervolar", true);
    setMeta("og:description", "Complete IVF management system — EMR, cycle tracking, CRM, billing, inventory. Built for how fertility clinics actually work.", true);
    setMeta("og:image", "https://www.hypervolar.com/og-ivf.jpg", true);

    const injectJSON = (id, obj) => {
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("script"); el.id = id; el.type = "application/ld+json"; document.head.appendChild(el); }
      el.textContent = JSON.stringify(obj);
    };
    injectJSON("ld-ivf-product", {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Hypervolar IVF Management System",
      "applicationCategory": "HealthApplication",
      "operatingSystem": "Web",
      "description": "Complete IVF clinic management software covering EMR, cycle tracking, CRM, billing, and inventory management for fertility clinics.",
      "url": "https://www.hypervolar.com/ivf-software",
      "offers": { "@type": "Offer", "availability": "https://schema.org/InStock" },
      "provider": { "@type": "Organization", "name": "Hypervolar", "url": "https://www.hypervolar.com" }
    });
    injectJSON("ld-ivf-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is IVF software?", "acceptedAnswer": { "@type": "Answer", "text": "IVF software is a specialized clinic management system built for fertility and IVF clinics. It covers patient records (EMR), stimulation and cycle tracking, embryology lab management, counseling CRM, billing, and inventory — replacing manual processes and fragmented tools." } },
        { "@type": "Question", "name": "How does an IVF management system work?", "acceptedAnswer": { "@type": "Answer", "text": "An IVF management system digitizes the entire fertility treatment workflow — from the first patient inquiry through counseling, stimulation monitoring, egg retrieval, embryology, transfer, and follow-up. Staff manage every step in one platform, with automated reminders and real-time reporting." } },
        { "@type": "Question", "name": "Is Hypervolar's IVF software customizable?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. We customize the system to match your clinic's specific protocols, treatment packages, reporting formats, and staff workflows. Unlike off-the-shelf products, our solution is built around how your clinic actually operates." } }
      ]
    });
  }, []);
  return null;
}

// ─── FONT ──────────────────────────────────────────────────────────────────────
const FontLink = () => {
  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Sans:wght@300;400;500;600&display=swap";
    document.head.appendChild(l);
  }, []);
  return null;
};

// ─── ICONS ────────────────────────────────────────────────────────────────────
const IC = {
  ClipBoard: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/></svg>,
  Activity: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  Heart: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  Dollar: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  Package: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  Users: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Check: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  AlertCircle: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  Arrow: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  ArrowDiag: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>,
  Phone: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.59 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.73a16 16 0 0 0 6.29 6.29l.87-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/></svg>,
  Mail: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  Microscope: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 18h8"/><path d="M3 21h18"/><path d="M14 21v-4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v4"/><path d="M14 7v1a3 3 0 0 1-3 3H9"/><path d="M9 11V7"/><path d="M9 7H7"/><path d="M7 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/><path d="M12 3 9 3"/></svg>,
  ChartBar: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
  Quote: () => <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>,
  WhatsApp: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>,
  X: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Menu: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
};

// ─── ANIMATED COUNTER ────────────────────────────────────────────────────────
function AnimCounter({ target, suffix = "", duration = 1600 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const start = Date.now();
        const step = () => {
          const progress = Math.min((Date.now() - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(step); else setCount(target);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
}

// ─── REVEAL ──────────────────────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".rv, .rv-l, .rv-r, .rv-s");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("vis"); io.unobserve(e.target); } }),
      { threshold: 0.07 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const CSS = `
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  :root{
    --ink:#0d1117;--page:#f8f7f4;--surf:#ffffff;--surf2:#f0efe9;
    --border:#e5e3da;--border2:#d4d2c8;
    --green:#1a6b4a;--green2:#2d8a60;--green-pale:#eaf4ef;--green-mid:#c2e0d1;
    --navy:#1a2744;--navy2:#243358;
    --txt:#1a1a1a;--txt2:#4a4a52;--txt3:#8a8a96;--white:#ffffff;
    --D:'Playfair Display',serif;--B:'DM Sans',sans-serif;
    --r:12px;--r2:20px;
  }
  html{scroll-behavior:smooth}
  body{background:var(--page);color:var(--txt);font-family:var(--B);overflow-x:hidden;-webkit-font-smoothing:antialiased}
  ::selection{background:var(--green-mid);color:var(--navy)}
  ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:var(--page)}::-webkit-scrollbar-thumb{background:var(--border2);border-radius:4px}
  a{color:inherit;text-decoration:none}

  @keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
  @keyframes pulse-ring{0%{transform:scale(1);opacity:.5}100%{transform:scale(1.9);opacity:0}}
  @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
  @keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes glow{0%,100%{box-shadow:0 0 0 0 rgba(26,107,74,0)}50%{box-shadow:0 0 30px 4px rgba(26,107,74,.12)}}

  .rv{opacity:0;transform:translateY(28px);transition:opacity .7s cubic-bezier(.16,1,.3,1),transform .7s cubic-bezier(.16,1,.3,1)}
  .rv-l{opacity:0;transform:translateX(-28px);transition:opacity .7s cubic-bezier(.16,1,.3,1),transform .7s cubic-bezier(.16,1,.3,1)}
  .rv-r{opacity:0;transform:translateX(28px);transition:opacity .7s cubic-bezier(.16,1,.3,1),transform .7s cubic-bezier(.16,1,.3,1)}
  .rv-s{opacity:0;transform:scale(.92);transition:opacity .7s cubic-bezier(.16,1,.3,1),transform .7s cubic-bezier(.16,1,.3,1)}
  .rv.vis,.rv-l.vis,.rv-r.vis,.rv-s.vis{opacity:1;transform:none}

  /* NAV */
  .nav{position:fixed;top:0;inset-inline:0;z-index:200;display:flex;align-items:center;justify-content:space-between;padding:0 5%;height:64px;transition:background .4s,box-shadow .3s}
  .nav.stuck{background:rgba(248,247,244,.95);backdrop-filter:blur(24px);box-shadow:0 1px 0 var(--border)}
  .logo{font-family:var(--D);font-size:1.45rem;font-weight:700;color:var(--navy);letter-spacing:.02em}
  .logo span{color:var(--green)}
  .nav-links{display:flex;gap:2rem;list-style:none}
  .nav-links a{color:var(--txt2);font-size:.84rem;font-weight:500;letter-spacing:.02em;transition:color .2s;position:relative}
  .nav-links a::after{content:'';position:absolute;bottom:-4px;left:0;width:100%;height:1.5px;background:var(--green);transform:scaleX(0);transform-origin:left;transition:transform .3s cubic-bezier(.16,1,.3,1)}
  .nav-links a:hover{color:var(--navy)}.nav-links a:hover::after{transform:scaleX(1)}
  .nav-btns{display:flex;gap:.75rem;align-items:center}
  .nb-wa{display:flex;align-items:center;gap:6px;padding:.55rem 1.2rem;border-radius:8px;background:#25d366;color:#fff;font-size:.8rem;font-weight:600;font-family:var(--B);cursor:pointer;border:none;transition:all .2s;letter-spacing:.01em}
  .nb-wa:hover{background:#20bd5a;transform:translateY(-1px)}
  .nb-demo{padding:.55rem 1.2rem;border-radius:8px;background:var(--navy);color:var(--white);font-size:.8rem;font-weight:600;font-family:var(--B);cursor:pointer;border:none;transition:all .2s;letter-spacing:.01em;position:relative;overflow:hidden}
  .nb-demo::before{content:'';position:absolute;inset:0;background:var(--green);transform:translateX(-100%);transition:transform .35s cubic-bezier(.16,1,.3,1)}
  .nb-demo:hover::before{transform:translateX(0)}
  .nb-demo span{position:relative;z-index:1}
  .hamburger{display:none;background:none;border:none;cursor:pointer;color:var(--navy);padding:4px;align-items:center;justify-content:center}
  .mob-drawer{display:none;position:fixed;top:64px;left:0;right:0;bottom:0;background:rgba(248,247,244,.98);backdrop-filter:blur(20px);z-index:199;flex-direction:column;padding:2rem 5%;border-top:1px solid var(--border);overflow-y:auto}
  .mob-drawer.open{display:flex}
  .mob-links{list-style:none;display:flex;flex-direction:column;gap:0}
  .mob-links li a{display:block;padding:1rem 0;font-size:1.05rem;font-weight:500;color:var(--txt);border-bottom:1px solid var(--border);transition:color .2s,padding-left .2s}
  .mob-links li a:hover{color:var(--green);padding-left:.5rem}
  .mob-ctas{display:flex;flex-direction:column;gap:.75rem;margin-top:2rem}
  .mob-ctas button{width:100%;padding:.9rem;border-radius:10px;font-weight:600;font-size:1rem;font-family:var(--B);border:none;cursor:pointer}
  .mob-wa{background:#25d366;color:#fff}
  .mob-demo{background:var(--navy);color:var(--white)}

  /* SECTION UTILITIES */
  .sec{padding:88px 5%}
  .inner{max-width:1160px;margin:0 auto}
  .label{display:inline-flex;align-items:center;gap:8px;font-size:.7rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--green);margin-bottom:1rem}
  .label-line{width:28px;height:1.5px;background:var(--green);flex-shrink:0}
  .sec-h{font-family:var(--D);font-size:clamp(1.9rem,4vw,3.2rem);color:var(--navy);font-weight:700;line-height:1.1;letter-spacing:-.01em;margin-bottom:1rem}
  .sec-p{font-size:.96rem;color:var(--txt2);line-height:1.9;font-weight:300}
  .center{text-align:center}
  .dot-g{width:6px;height:6px;border-radius:50%;background:var(--green);animation:blink 1.4s infinite;flex-shrink:0;display:inline-block}

  /* HERO */
  .hero{min-height:100svh;display:flex;flex-direction:column;justify-content:center;padding:120px 5% 80px;position:relative;overflow:hidden;background:var(--surf)}
  .hero-bg{position:absolute;inset:0;z-index:0}
  .hero-grid{position:absolute;inset:0;background-image:radial-gradient(var(--border) 1px,transparent 1px);background-size:30px 30px;opacity:.45}
  .hero-blob{position:absolute;border-radius:50%;filter:blur(90px);opacity:.08}
  .b1{width:700px;height:700px;background:var(--green);top:-200px;right:-100px}
  .b2{width:500px;height:500px;background:var(--navy);bottom:-150px;left:-80px}
  .hero-in{position:relative;z-index:2;max-width:1160px;margin:0 auto;width:100%;display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center}
  .hero-left{animation:fadeUp .8s ease both}
  .eyebrow{display:inline-flex;align-items:center;gap:8px;padding:6px 18px;border-radius:100px;background:var(--green-pale);border:1px solid var(--green-mid);font-size:.7rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--green);margin-bottom:1.5rem}
  .hero-h1{font-family:var(--D);font-size:clamp(2.4rem,4.5vw,3.8rem);line-height:1.05;letter-spacing:-.02em;color:var(--navy);margin-bottom:1.2rem;font-weight:700}
  .hero-h1 em{font-style:normal;background:linear-gradient(135deg,var(--green),var(--navy));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;background-size:200% auto;animation:shimmer 5s linear infinite}
  .hero-p{font-size:1rem;font-weight:300;color:var(--txt2);max-width:520px;margin-bottom:2.5rem;line-height:1.9}
  .hero-ctas{display:flex;gap:1rem;flex-wrap:wrap}
  .btn-demo{display:inline-flex;align-items:center;gap:8px;padding:.9rem 2rem;border-radius:8px;background:var(--navy);border:none;color:var(--white);font-weight:600;font-size:.88rem;font-family:var(--B);cursor:pointer;position:relative;overflow:hidden;letter-spacing:.01em;transition:transform .2s}
  .btn-demo::before{content:'';position:absolute;inset:0;background:var(--green);transform:translateX(-100%);transition:transform .4s cubic-bezier(.16,1,.3,1)}
  .btn-demo:hover::before{transform:translateX(0)}
  .btn-demo:hover{transform:translateY(-2px)}
  .btn-demo>*{position:relative;z-index:1}
  .btn-wa{display:inline-flex;align-items:center;gap:8px;padding:.9rem 2rem;border-radius:8px;background:#25d366;border:none;color:#fff;font-weight:600;font-size:.88rem;font-family:var(--B);cursor:pointer;transition:all .2s;letter-spacing:.01em}
  .btn-wa:hover{background:#20bd5a;transform:translateY(-2px)}
  .hero-trust{display:flex;align-items:center;gap:1.25rem;margin-top:2rem;flex-wrap:wrap}
  .trust-badge{display:flex;align-items:center;gap:6px;font-size:.75rem;font-weight:500;color:var(--txt3)}
  .trust-badge svg{color:var(--green)}
  .hero-right{animation:fadeIn .8s .2s ease both;position:relative}
  .hero-card{background:var(--navy);border-radius:var(--r2);padding:2rem;color:var(--white);animation:float 6s ease-in-out infinite}
  .hc-label{font-size:.65rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.35);margin-bottom:.75rem}
  .hc-title{font-family:var(--D);font-size:1.5rem;font-weight:700;line-height:1.15;margin-bottom:1.25rem}
  .hc-title span{color:var(--green2)}
  .hc-modules{display:flex;flex-direction:column;gap:.5rem}
  .hc-mod{display:flex;align-items:center;gap:.75rem;padding:.75rem 1rem;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.08);border-radius:8px;font-size:.82rem;color:rgba(255,255,255,.75);font-weight:500;transition:all .25s;cursor:default}
  .hc-mod:hover{background:rgba(255,255,255,.1);transform:translateX(4px)}
  .hc-mod-ic{width:28px;height:28px;border-radius:6px;background:rgba(45,138,96,.25);border:1px solid rgba(45,138,96,.35);display:flex;align-items:center;justify-content:center;color:var(--green2);flex-shrink:0}
  .live-chip{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:100px;background:rgba(26,107,74,.2);border:1px solid rgba(26,107,74,.35);font-size:.65rem;font-weight:700;color:rgba(255,255,255,.75);margin-top:1.25rem;letter-spacing:.06em}
  .pulse-dot{width:7px;height:7px;border-radius:50%;background:var(--green2);display:inline-block;position:relative;flex-shrink:0}
  .pulse-dot::after{content:'';position:absolute;inset:-4px;border-radius:50%;border:2px solid var(--green2);animation:pulse-ring 1.5s ease-out infinite}
  .hero-float-card{position:absolute;background:var(--surf);border:1px solid var(--border);border-radius:var(--r);padding:.85rem 1.1rem;box-shadow:0 8px 28px rgba(0,0,0,.1)}
  .hfc1{top:-30px;left:-40px;width:160px;animation:float 5s ease-in-out infinite;animation-delay:-1s}
  .hfc2{bottom:-30px;right:-50px;width:160px;animation:float 7s ease-in-out infinite;animation-delay:-3s}
  .hf-l{font-size:.62rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--txt3)}
  .hf-v{font-size:1.25rem;font-weight:700;font-family:var(--D);color:var(--navy);margin-top:2px}
  .hf-s{font-size:.68rem;color:var(--txt3)}

  /* TICKER */
  .ticker-wrap{border-top:1px solid var(--border);background:var(--surf2);height:42px;overflow:hidden;display:flex;align-items:center}
  .ticker-track{display:flex;white-space:nowrap;animation:ticker 28s linear infinite}
  .tick-item{padding:0 1.5rem;font-size:.7rem;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:var(--txt3);border-right:1px solid var(--border);flex-shrink:0;display:flex;align-items:center;gap:8px}
  .t-dot{width:3px;height:3px;border-radius:50%;background:var(--green)}

  /* STATS STRIP */
  .stats-strip{background:var(--navy);padding:60px 5%}
  .stats-row{max-width:1160px;margin:0 auto;display:grid;grid-template-columns:repeat(4,1fr);gap:2rem}
  .stat-box{text-align:center;padding:1.5rem;border-right:1px solid rgba(255,255,255,.08);position:relative}
  .stat-box:last-child{border-right:none}
  .stat-n{font-family:var(--D);font-size:3rem;font-weight:700;color:var(--white);line-height:1;display:block}
  .stat-l{font-size:.72rem;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.4);margin-top:.5rem}
  .stat-d{font-size:.78rem;color:rgba(255,255,255,.3);margin-top:.25rem}

  /* PROBLEM */
  .problem{background:var(--page)}
  .prob-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem;margin-top:3rem}
  .prob-card{background:var(--surf);border:1px solid var(--border);border-radius:var(--r2);padding:2rem;transition:all .3s;position:relative;overflow:hidden;cursor:default}
  .prob-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#e8443b,#f28c28);border-radius:3px 3px 0 0;transform:scaleX(0);transform-origin:left;transition:transform .4s cubic-bezier(.16,1,.3,1)}
  .prob-card:hover::before{transform:scaleX(1)}
  .prob-card:hover{border-color:var(--border2);transform:translateY(-4px);box-shadow:0 16px 40px rgba(0,0,0,.08)}
  .prob-n{font-size:.68rem;font-weight:700;letter-spacing:.1em;color:var(--txt3);margin-bottom:.75rem}
  .prob-ic{width:42px;height:42px;border-radius:10px;background:#fef2f2;border:1px solid #fed7d7;display:flex;align-items:center;justify-content:center;color:#e8443b;margin-bottom:1rem;transition:all .3s}
  .prob-card:hover .prob-ic{background:#e8443b;color:#fff;border-color:#e8443b}
  .prob-t{font-size:.95rem;font-weight:600;color:var(--navy);margin-bottom:.5rem}
  .prob-d{font-size:.82rem;color:var(--txt2);line-height:1.8}

  /* SOLUTION */
  .solution{background:var(--surf)}
  .sol-inner{display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center}
  .sol-list{display:flex;flex-direction:column;gap:.75rem;margin-top:1.75rem}
  .sol-item{display:flex;align-items:center;gap:.875rem;padding:1rem 1.25rem;background:var(--surf2);border:1px solid var(--border);border-radius:var(--r);transition:all .3s;cursor:default}
  .sol-item:hover{border-color:var(--green-mid);background:var(--green-pale);transform:translateX(8px)}
  .sol-check{width:30px;height:30px;border-radius:50%;background:var(--green-pale);border:1px solid var(--green-mid);display:flex;align-items:center;justify-content:center;color:var(--green);flex-shrink:0;transition:all .3s}
  .sol-item:hover .sol-check{background:var(--green);color:#fff;border-color:var(--green)}
  .sol-t{font-size:.88rem;font-weight:600;color:var(--navy)}
  .sol-visual{background:var(--navy);border-radius:var(--r2);padding:2.5rem;position:relative;overflow:hidden;animation:glow 4s ease-in-out infinite}
  .sol-visual::before{content:'';position:absolute;top:-80px;right:-80px;width:220px;height:220px;border-radius:50%;background:rgba(255,255,255,.03)}
  .sv-label{font-size:.65rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.3);margin-bottom:1rem}
  .sv-title{font-family:var(--D);font-size:1.6rem;font-weight:700;color:var(--white);line-height:1.2;margin-bottom:1.5rem}
  .sv-title span{color:var(--green2)}
  .sv-pills{display:flex;flex-wrap:wrap;gap:.5rem}
  .sv-pill{padding:5px 12px;border-radius:100px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.06);font-size:.7rem;font-weight:600;letter-spacing:.04em;color:rgba(255,255,255,.65);transition:all .2s;cursor:default}
  .sv-pill:hover{background:rgba(255,255,255,.15);border-color:rgba(255,255,255,.3);transform:translateY(-2px)}

  /* FEATURES */
  .features{background:var(--page)}
  .feat-hdr{text-align:center;margin-bottom:4rem}
  .modules-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem}
  .mod-card{background:var(--surf);border:1px solid var(--border);border-radius:var(--r2);padding:2rem;transition:all .3s;position:relative;overflow:hidden;cursor:default}
  .mod-card::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--green),var(--navy));transform:scaleX(0);transform-origin:left;transition:transform .4s cubic-bezier(.16,1,.3,1)}
  .mod-card:hover::after{transform:scaleX(1)}
  .mod-card:hover{border-color:var(--green-mid);transform:translateY(-5px);box-shadow:0 20px 50px rgba(26,107,74,.1)}
  .mod-ic{width:48px;height:48px;border-radius:12px;background:var(--green-pale);border:1px solid var(--green-mid);display:flex;align-items:center;justify-content:center;color:var(--green);margin-bottom:1.25rem;transition:all .3s}
  .mod-card:hover .mod-ic{background:var(--green);color:#fff;border-color:var(--green);transform:rotate(5deg)}
  .mod-t{font-size:1rem;font-weight:700;color:var(--navy);margin-bottom:1rem}
  .mod-bullets{list-style:none;display:flex;flex-direction:column;gap:.6rem}
  .mod-bullets li{display:flex;align-items:flex-start;gap:.6rem;font-size:.81rem;color:var(--txt2);line-height:1.65}
  .mod-bullets li::before{content:'→';color:var(--green);font-weight:700;flex-shrink:0;margin-top:1px}

  /* WORKFLOW */
  .workflow{background:var(--navy)}
  .wf-hdr{text-align:center;margin-bottom:4rem}
  .wf-hdr .label{justify-content:center;color:var(--green2)}
  .wf-hdr .label-line{background:var(--green2)}
  .wf-steps{display:flex;flex-direction:column;gap:0}
  .wf-step{display:grid;grid-template-columns:80px 1fr;gap:2rem;align-items:start;position:relative}
  .wf-step:not(:last-child)::before{content:'';position:absolute;left:40px;top:56px;width:1px;height:calc(100% - 8px);background:rgba(255,255,255,.1);transform:translateX(-50%)}
  .wf-num-wrap{display:flex;flex-direction:column;align-items:center;gap:0;flex-shrink:0}
  .wf-num{width:52px;height:52px;border-radius:50%;background:rgba(26,107,74,.25);border:1.5px solid rgba(26,107,74,.5);display:flex;align-items:center;justify-content:center;font-family:var(--D);font-size:1.1rem;font-weight:700;color:var(--green2);transition:all .3s;flex-shrink:0}
  .wf-step:hover .wf-num{background:var(--green);border-color:var(--green);color:#fff;transform:scale(1.1)}
  .wf-body{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:var(--r);padding:1.5rem;margin-bottom:1.5rem;transition:all .3s;cursor:default}
  .wf-step:hover .wf-body{background:rgba(255,255,255,.08);border-color:rgba(26,107,74,.35)}
  .wf-phase{font-size:.65rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--green2);margin-bottom:.5rem}
  .wf-title{font-size:1rem;font-weight:600;color:var(--white);margin-bottom:.4rem}
  .wf-desc{font-size:.82rem;color:rgba(255,255,255,.5);line-height:1.75}

  /* BENEFITS */
  .benefits{background:var(--surf)}
  .ben-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--border);border-radius:var(--r2);overflow:hidden;border:1px solid var(--border)}
  .ben-card{background:var(--surf);padding:2.5rem 1.75rem;text-align:center;transition:all .3s;cursor:default}
  .ben-card:hover{background:var(--green-pale)}
  .ben-ic{display:flex;justify-content:center;margin-bottom:1rem;color:var(--border2);transition:color .3s}
  .ben-card:hover .ben-ic{color:var(--green)}
  .ben-n{font-family:var(--D);font-size:2.8rem;font-weight:700;color:var(--navy);line-height:1;margin-bottom:.4rem}
  .ben-l{font-size:.85rem;font-weight:600;color:var(--txt2);margin-bottom:.4rem}
  .ben-d{font-size:.76rem;color:var(--txt3);line-height:1.65}

  /* WHY */
  .why{background:var(--page)}
  .why-inner{display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center}
  .why-points{display:flex;flex-direction:column;gap:1rem;margin-top:2rem}
  .why-pt{display:flex;align-items:flex-start;gap:1.25rem;padding:1.25rem;background:var(--surf);border:1px solid var(--border);border-radius:var(--r);transition:all .3s;cursor:default}
  .why-pt:hover{border-color:var(--green-mid);transform:translateX(8px);box-shadow:4px 0 20px rgba(26,107,74,.07)}
  .wp-ic{width:42px;height:42px;border-radius:10px;background:var(--green-pale);border:1px solid var(--green-mid);display:flex;align-items:center;justify-content:center;color:var(--green);flex-shrink:0;transition:all .3s}
  .why-pt:hover .wp-ic{background:var(--green);color:#fff;border-color:var(--green)}
  .wp-t{font-size:.9rem;font-weight:600;color:var(--navy);margin-bottom:4px}
  .wp-d{font-size:.8rem;color:var(--txt2);line-height:1.7}
  .why-quote{background:var(--navy);border-radius:var(--r2);padding:2.5rem;position:relative;overflow:hidden}
  .why-quote::before{content:'';position:absolute;top:-60px;right:-60px;width:180px;height:180px;border-radius:50%;background:rgba(255,255,255,.03)}
  .wq-ico{color:var(--green2);opacity:.3;margin-bottom:1rem}
  .wq-text{font-family:var(--D);font-size:1.25rem;font-weight:400;color:var(--white);line-height:1.65;font-style:italic;margin-bottom:1.5rem}
  .wq-author{display:flex;align-items:center;gap:1rem;padding-top:1.5rem;border-top:1px solid rgba(255,255,255,.08)}
  .wq-av{width:44px;height:44px;border-radius:50%;background:var(--green);display:flex;align-items:center;justify-content:center;font-family:var(--D);font-size:1.1rem;font-weight:700;color:#fff;flex-shrink:0}
  .wq-name{font-size:.88rem;font-weight:600;color:var(--white)}
  .wq-co{font-size:.72rem;color:rgba(255,255,255,.4)}

  /* FAQ */
  .faq{background:var(--surf)}
  .faq-inner{display:grid;grid-template-columns:1fr 1.4fr;gap:5rem;align-items:start}
  .faq-list{display:flex;flex-direction:column;gap:.875rem}
  .faq-item{background:var(--surf2);border:1px solid var(--border);border-radius:var(--r);overflow:hidden;transition:border-color .25s}
  .faq-item.open{border-color:var(--green-mid);background:var(--green-pale)}
  .faq-q{display:flex;align-items:center;justify-content:space-between;padding:1.2rem 1.5rem;cursor:pointer;font-size:.91rem;font-weight:600;color:var(--navy);gap:1rem;user-select:none;transition:color .2s}
  .faq-item.open .faq-q{color:var(--green)}
  .faq-arrow{font-size:1.1rem;color:var(--txt3);transition:transform .3s cubic-bezier(.16,1,.3,1),color .2s;flex-shrink:0;line-height:1}
  .faq-item.open .faq-arrow{transform:rotate(180deg);color:var(--green)}
  .faq-a{font-size:.84rem;color:var(--txt2);line-height:1.9;padding:0 1.5rem 1.25rem;display:none}
  .faq-item.open .faq-a{display:block}

  /* FINAL CTA */
  .final-cta{background:linear-gradient(135deg,var(--green) 0%,var(--navy) 100%);padding:88px 5%}
  .cta-in{max-width:800px;margin:0 auto;text-align:center}
  .cta-in .sec-h{color:var(--white);margin-bottom:1rem}
  .cta-in .sec-p{color:rgba(255,255,255,.65);margin-bottom:2.5rem;max-width:560px;margin-left:auto;margin-right:auto}
  .cta-btns{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap}
  .btn-white{padding:.95rem 2.25rem;border-radius:8px;border:none;background:var(--white);color:var(--navy);font-weight:700;font-size:.9rem;font-family:var(--B);cursor:pointer;transition:all .3s;letter-spacing:.01em}
  .btn-white:hover{background:var(--green-pale);transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.2)}
  .btn-ow{padding:.95rem 2.25rem;border-radius:8px;border:1.5px solid rgba(255,255,255,.4);background:transparent;color:var(--white);font-size:.9rem;font-weight:500;font-family:var(--B);cursor:pointer;transition:all .3s;display:flex;align-items:center;gap:8px}
  .btn-ow:hover{background:rgba(255,255,255,.1);border-color:rgba(255,255,255,.7)}

  /* FOOTER */
  footer{background:var(--navy);padding:3rem 5%}
  .foot-in{max-width:1160px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:2rem}
  .foot-logo{font-family:var(--D);font-size:1.4rem;font-weight:700;color:var(--white)}
  .foot-logo span{color:var(--green2)}
  .foot-tag{font-size:.7rem;color:rgba(255,255,255,.3);margin-top:4px}
  .foot-links{display:flex;gap:2rem;flex-wrap:wrap}
  .foot-links a{color:rgba(255,255,255,.45);font-size:.8rem;font-weight:500;transition:color .2s}
  .foot-links a:hover{color:var(--white)}
  .copy{width:100%;text-align:center;font-size:.7rem;color:rgba(255,255,255,.2);padding-top:1.5rem;border-top:1px solid rgba(255,255,255,.06);margin-top:1rem}

  @media(max-width:1024px){
    .hero-in{grid-template-columns:1fr;gap:3rem}
    .hero-right{display:none}
    .sol-inner,.why-inner,.faq-inner{grid-template-columns:1fr;gap:3rem}
    .modules-grid{grid-template-columns:repeat(2,1fr)}
    .prob-grid{grid-template-columns:repeat(2,1fr)}
    .ben-grid{grid-template-columns:repeat(2,1fr)}
    .stats-row{grid-template-columns:repeat(2,1fr)}
    .stat-box{border-right:none;border-bottom:1px solid rgba(255,255,255,.08)}
  }
  @media(max-width:768px){
    .nav-links,.nav-btns{display:none}
    .hamburger{display:flex}
    .sec{padding:64px 4%}
    .prob-grid,.modules-grid{grid-template-columns:1fr}
    .ben-grid{grid-template-columns:1fr 1fr}
    .wf-step{grid-template-columns:56px 1fr}
    .hero-ctas{flex-direction:column;align-items:flex-start}
  }
  @media(max-width:480px){
    .hero{padding:100px 4% 70px}
    .hero-h1{font-size:2.4rem}
    .sec-h{font-size:1.9rem}
    .stats-row{grid-template-columns:1fr 1fr}
    .ben-grid{grid-template-columns:1fr}
    .cta-btns{flex-direction:column;align-items:center}
    .btn-white,.btn-ow{width:100%;max-width:320px;justify-content:center}
    .why-inner{gap:2rem}
  }
`;

// ─── NAV ─────────────────────────────────────────────────────────────────────
function Nav() {
  const [stuck, setStuck] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { const h = () => setStuck(window.scrollY > 30); window.addEventListener("scroll", h); return () => window.removeEventListener("scroll", h); }, []);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  const go = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setOpen(false); };
  const ITEMS = [
    { id: "features", label: "Features" },
    { id: "workflow", label: "IVF Workflow" },
    { id: "why-us", label: "Why Us" },
    { id: "faq", label: "FAQ" },
    { id: "contact", label: "Contact" },
  ];
  const wa = () => window.open("https://wa.me/917827436219?text=Hi%2C%20I%27d%20like%20to%20know%20more%20about%20your%20IVF%20software", "_blank");
  return (
    <>
      <nav className={`nav${stuck ? " stuck" : ""}`}>
        <a href="https://www.hypervolar.com" className="logo">HYPER<span>VOLAR</span></a>
        <ul className="nav-links">{ITEMS.map(({ id, label }) => <li key={id}><a href={`#${id}`} onClick={e => { e.preventDefault(); go(id); }}>{label}</a></li>)}</ul>
        <div className="nav-btns">
          <button className="nb-wa" onClick={wa}><IC.WhatsApp /> WhatsApp</button>
          <button className="nb-demo" onClick={() => go("contact")}><span>Book Demo</span></button>
        </div>
        <button className="hamburger" onClick={() => setOpen(o => !o)}>{open ? <IC.X /> : <IC.Menu />}</button>
      </nav>
      <div className={`mob-drawer${open ? " open" : ""}`}>
        <ul className="mob-links">{ITEMS.map(({ id, label }) => <li key={id}><a href={`#${id}`} onClick={e => { e.preventDefault(); go(id); }}>{label}</a></li>)}</ul>
        <div className="mob-ctas">
          <button className="mob-ctas mob-wa" onClick={wa}>WhatsApp Us →</button>
          <button className="mob-ctas mob-demo" onClick={() => go("contact")}>Book a Demo →</button>
        </div>
      </div>
    </>
  );
}

// ─── HERO ────────────────────────────────────────────────────────────────────
const TICKS = ["IVF Software", "EMR & Patient Records", "Cycle Tracking", "Counseling CRM", "Billing & Packages", "Inventory Management", "Live at Aveya IVF", "5+ Years in Healthcare IT"];

function Hero() {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const wa = () => window.open("https://wa.me/917827436219?text=Hi%2C%20I%27d%20like%20to%20know%20more%20about%20your%20IVF%20software", "_blank");
  const items = [...TICKS, ...TICKS];
  const MODULES = [
    { Icon: IC.ClipBoard, label: "EMR & Patient Records" },
    { Icon: IC.Activity, label: "IVF Cycle Tracking" },
    { Icon: IC.Heart, label: "Counseling CRM" },
    { Icon: IC.Dollar, label: "Billing & Packages" },
    { Icon: IC.Package, label: "Inventory Management" },
    { Icon: IC.Microscope, label: "Embryology Lab" },
  ];
  return (
    <>
      <section id="hero" className="hero">
        <div className="hero-bg">
          <div className="hero-grid" />
          <div className="hero-blob b1" />
          <div className="hero-blob b2" />
        </div>
        <div className="hero-in">
          {/* Left */}
          <div className="hero-left">
            <div className="eyebrow"><span className="dot-g" />Purpose-Built for IVF Clinics · Live in Production</div>
            <h1 className="hero-h1">
              Complete <em>IVF Software</em><br />
              for Fertility Clinics
            </h1>
            <p className="hero-p">
              A full-stack <strong>IVF clinic management system</strong> covering patient records, cycle tracking, counseling CRM, billing, and inventory — built around how fertility clinics actually work.
            </p>
            <div className="hero-ctas">
              <button className="btn-demo" onClick={() => go("contact")}>
                <span>Book a Free Demo</span>
                <IC.Arrow />
              </button>
              <button className="btn-wa" onClick={wa}>
                <IC.WhatsApp />
                <span>WhatsApp Us</span>
              </button>
            </div>
            <div className="hero-trust">
              {["Live at Aveya IVF", "5+ Years Healthcare IT", "20+ Modules Delivered"].map((t) => (
                <span className="trust-badge" key={t}><IC.Check />{t}</span>
              ))}
            </div>
          </div>
          {/* Right */}
          <div className="hero-right">
            <div className="hero-float-card hfc1">
              <div className="hf-l">Active Patients</div>
              <div className="hf-v">340+</div>
              <div className="hf-s">tracked in real time</div>
            </div>
            <div className="hero-card">
              <div className="hc-label">Hypervolar IVF Software</div>
              <div className="hc-title">One System.<br /><span>Every Stage</span> of IVF.</div>
              <div className="hc-modules">
                {MODULES.map((m) => (
                  <div className="hc-mod" key={m.label}>
                    <div className="hc-mod-ic"><m.Icon /></div>
                    {m.label}
                  </div>
                ))}
              </div>
              <div className="live-chip"><span className="pulse-dot" style={{ position: "static" }} />Live at Aveya IVF Clinic</div>
            </div>
            <div className="hero-float-card hfc2">
              <div className="hf-l">Cycle Success Rate</div>
              <div className="hf-v">↑ 18%</div>
              <div className="hf-s">after system adoption</div>
            </div>
          </div>
        </div>
      </section>
      {/* Ticker */}
      <div className="ticker-wrap">
        <div className="ticker-track">{items.map((t, i) => <div className="tick-item" key={i}><span className="t-dot" />{t}</div>)}</div>
      </div>
    </>
  );
}

// ─── STATS STRIP ─────────────────────────────────────────────────────────────
function StatsStrip() {
  return (
    <section className="stats-strip">
      <div className="stats-row">
        {[
          { n: 1, s: "", l: "Complete IVF System", d: "end-to-end, live in clinics" },
          { n: 5, s: "+", l: "Years in Healthcare IT", d: "deep domain expertise" },
          { n: 20, s: "+", l: "Modules Delivered", d: "across all domains" },
          { n: 10, s: "+", l: "Clinics & Companies", d: "trusting our software" },
        ].map((st) => (
          <div className="stat-box rv" key={st.l}>
            <span className="stat-n"><AnimCounter target={st.n} suffix={st.s} /></span>
            <div className="stat-l">{st.l}</div>
            <div className="stat-d">{st.d}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── PROBLEM ─────────────────────────────────────────────────────────────────
const PROBLEMS = [
  { n: "01", Icon: IC.ClipBoard, t: "Manual Cycle Tracking", d: "Staff track stimulation days, follicle counts, and hormone levels on paper or Excel — leading to missed updates, transcription errors, and no audit trail." },
  { n: "02", Icon: IC.Users, t: "Missed Follow-Ups & Leads", d: "Potential patients who inquire never get timely callbacks. Existing patients miss reminder calls. No system to manage counseling pipelines or automate follow-ups." },
  { n: "03", Icon: IC.ChartBar, t: "Data Scattered Across Tools", d: "Patient records in one place, billing in another, inventory in Excel, reports requested manually. No single source of truth for clinic operations." },
  { n: "04", Icon: IC.Dollar, t: "Billing Errors & Package Confusion", d: "IVF packages are complex — multiple procedures, medicines, and add-ons. Manual billing leads to revenue leakage, patient disputes, and failed reconciliation." },
  { n: "05", Icon: IC.Package, t: "Inventory Without Real-Time Visibility", d: "Fertility drugs and consumables run out unnoticed. No system tracks medicine usage per cycle, expiry dates, or reorder thresholds automatically." },
  { n: "06", Icon: IC.AlertCircle, t: "Reporting Takes Hours", d: "Cycle outcome reports, patient statistics, and financial summaries are compiled manually — taking significant staff time with high risk of inaccuracy." },
];

function Problem() {
  return (
    <section id="problems" className="sec problem">
      <div className="inner">
        <div className="rv" style={{ marginBottom: "3rem" }}>
          <div className="label"><span className="label-line" />The Real Problem</div>
          <h2 className="sec-h">Why IVF Clinics Struggle<br />Without Dedicated Software</h2>
          <p className="sec-p" style={{ maxWidth: 580, marginTop: ".75rem" }}>
            Most fertility clinics are still running mission-critical workflows on general tools that were never designed for IVF. Here's what that actually costs.
          </p>
        </div>
        <div className="prob-grid">
          {PROBLEMS.map((p, i) => (
            <div className="prob-card rv" key={p.t} style={{ transitionDelay: `${i * 70}ms` }}>
              <div className="prob-n">{p.n}</div>
              <div className="prob-ic"><p.Icon /></div>
              <h3 className="prob-t">{p.t}</h3>
              <p className="prob-d">{p.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SOLUTION ────────────────────────────────────────────────────────────────
const SOL_ITEMS = [
  "Every patient cycle is tracked digitally — day by day, protocol by protocol",
  "Automated follow-up reminders eliminate missed counseling calls",
  "One unified platform: records, billing, inventory, and reporting",
  "IVF package billing with itemized breakdowns — zero revenue leakage",
  "Real-time inventory tracking per cycle with low-stock alerts",
  "One-click reports: outcomes, revenue, patient flow, and more",
];

function Solution() {
  return (
    <section id="solution" className="sec solution">
      <div className="inner">
        <div className="sol-inner">
          <div className="rv-l">
            <div className="label"><span className="label-line" />Our Solution</div>
            <h2 className="sec-h">A Complete IVF Management<br />System — Not a Workaround</h2>
            <p className="sec-p" style={{ marginTop: ".75rem" }}>
              Hypervolar's <strong>IVF clinic software</strong> replaces the patchwork of spreadsheets, paper records, and disconnected apps with a single, purpose-built platform. Every module is designed around how fertility clinics actually operate — not how generic HMS software thinks they should.
            </p>
            <ul className="sol-list">
              {SOL_ITEMS.map((item) => (
                <li className="sol-item" key={item}>
                  <span className="sol-check"><IC.Check /></span>
                  <span className="sol-t">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rv-r">
            <div className="sol-visual">
              <div className="sv-label">IVF Management System</div>
              <div className="sv-title">Built for<br /><span>IVF Workflows</span></div>
              <div className="sv-pills">
                {["IVF Cycle Tracking", "Patient EMR", "Stimulation Protocols", "Embryology Lab", "Counseling CRM", "Billing Engine", "Inventory Module", "Analytics Dashboard"].map((t) => (
                  <span className="sv-pill" key={t}>{t}</span>
                ))}
              </div>
              <div style={{ marginTop: "1.75rem", padding: "1.25rem", background: "rgba(26,107,74,.15)", border: "1px solid rgba(26,107,74,.3)", borderRadius: "var(--r)" }}>
                <div className="live-chip" style={{ marginTop: 0, marginBottom: ".75rem" }}><span className="pulse-dot" style={{ position: "static" }} />Deployed & Live</div>
                <div style={{ fontSize: ".83rem", color: "rgba(255,255,255,.6)", lineHeight: 1.7 }}>
                  Our IVF patient management software is actively used at <strong style={{ color: "rgba(255,255,255,.85)" }}>Aveya IVF clinic</strong> — handling daily patient management, cycle tracking, billing, and reporting.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FEATURES ────────────────────────────────────────────────────────────────
const MODULES = [
  {
    Icon: IC.Activity,
    t: "IVF Cycle Management",
    bullets: [
      "Day-by-day stimulation monitoring with protocol assignment",
      "Follicle measurement records and growth tracking",
      "Hormone level logs (E2, LH, Progesterone) per cycle day",
      "Egg retrieval and fertilization outcome documentation",
    ],
  },
  {
    Icon: IC.ClipBoard,
    t: "EMR & Patient Records",
    bullets: [
      "Structured digital records from first consultation to discharge",
      "Medical history, diagnosis, and investigation integration",
      "Customizable forms for IVF-specific clinical data capture",
      "Secure, searchable, and audit-ready patient database",
    ],
  },
  {
    Icon: IC.Heart,
    t: "Counseling CRM & Follow-Ups",
    bullets: [
      "Lead capture and inquiry tracking from first contact",
      "Counselor pipeline with status stages and conversion tracking",
      "Automated follow-up reminders via SMS or internal alerts",
      "Patient communication logs and appointment scheduling",
    ],
  },
  {
    Icon: IC.Dollar,
    t: "Billing & Package Management",
    bullets: [
      "IVF package creation with bundled procedure and medicine pricing",
      "Itemized invoice generation with add-on management",
      "Payment tracking, balance due, and receipt generation",
      "Financial reporting by patient, package, and time period",
    ],
  },
  {
    Icon: IC.Package,
    t: "Inventory Management",
    bullets: [
      "Real-time stock tracking for fertility drugs and consumables",
      "Medicine consumption linked to individual patient cycles",
      "Expiry date alerts and low-stock notifications",
      "Supplier management and purchase order tracking",
    ],
  },
  {
    Icon: IC.Microscope,
    t: "Embryology Lab Module",
    bullets: [
      "Embryo grading and development stage documentation",
      "Cryopreservation records with storage location tracking",
      "Sperm analysis and semen parameter logging",
      "Lab outcome reports linked to patient and cycle records",
    ],
  },
];

function Features() {
  return (
    <section id="features" className="sec features">
      <div className="inner">
        <div className="feat-hdr rv">
          <div className="label" style={{ justifyContent: "center" }}><span className="label-line" />Core Modules</div>
          <h2 className="sec-h">Every Feature Your IVF Clinic Needs —<br />Built Into One System</h2>
          <p className="sec-p" style={{ maxWidth: 560, margin: ".75rem auto 0" }}>
            Our <strong>IVF management system</strong> covers every clinical and operational module — from the embryology lab to the billing desk. No third-party patchwork. No missing features.
          </p>
        </div>
        <div className="modules-grid">
          {MODULES.map((m, i) => (
            <div className="mod-card rv" key={m.t} style={{ transitionDelay: `${i * 80}ms` }}>
              <div className="mod-ic"><m.Icon /></div>
              <h3 className="mod-t">{m.t}</h3>
              <ul className="mod-bullets">
                {m.bullets.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── WORKFLOW ────────────────────────────────────────────────────────────────
const WF_STEPS = [
  { phase: "Stage 01 — Acquisition", title: "Patient Inquiry & Registration", desc: "Inquiries from walk-ins, referrals, or digital channels are captured in the CRM. Lead source, contact details, and initial notes are logged — nothing slips through." },
  { phase: "Stage 02 — Consultation", title: "Counseling & Treatment Planning", desc: "Counselors manage patient consultations, document treatment recommendations, and convert inquiries into confirmed treatment plans — tracked in a dedicated counseling pipeline." },
  { phase: "Stage 03 — Stimulation", title: "Ovarian Stimulation Monitoring", desc: "Day-by-day stimulation protocols are recorded — including medicine dosages, follicle scan results, hormone levels, and protocol adjustments. All visible in a single patient timeline." },
  { phase: "Stage 04 — Procedure", title: "Egg Retrieval & Fertilization", desc: "Egg retrieval outcomes — number retrieved, mature, fertilized — are documented. The embryology lab module records sperm parameters and fertilization method used." },
  { phase: "Stage 05 — Embryology", title: "Embryo Development & Grading", desc: "Daily embryo development stages and grades are logged. Cryopreserved embryos are tracked by storage location, freeze date, and thaw records — with zero ambiguity." },
  { phase: "Stage 06 — Transfer", title: "Embryo Transfer & Post-Transfer Care", desc: "Transfer details — embryo grade, transfer type, number transferred, luteal support — are recorded. Post-transfer follow-up appointments and medication schedules are managed." },
  { phase: "Stage 07 — Outcome", title: "Pregnancy Test & Follow-Up", desc: "Beta-HCG results, pregnancy confirmation, and cycle outcome are recorded and linked to the full patient record. Outcome analytics feed into clinic performance reporting automatically." },
];

function Workflow() {
  return (
    <section id="workflow" className="sec workflow">
      <div className="inner">
        <div className="wf-hdr rv">
          <div className="label"><span className="label-line" />IVF Workflow</div>
          <h2 className="sec-h" style={{ color: "var(--white)" }}>Designed Around Real IVF Workflow</h2>
          <p className="sec-p" style={{ color: "rgba(255,255,255,.55)", maxWidth: 560, margin: ".75rem auto 0" }}>
            Our <strong style={{ color: "rgba(255,255,255,.8)" }}>IVF cycle tracking software</strong> follows the exact clinical journey — from first inquiry to final outcome. Every stage is covered, documented, and connected.
          </p>
        </div>
        <div className="wf-steps">
          {WF_STEPS.map((s, i) => (
            <div className="wf-step rv" key={s.title} style={{ transitionDelay: `${i * 60}ms` }}>
              <div className="wf-num-wrap">
                <div className="wf-num">{String(i + 1).padStart(2, "0")}</div>
              </div>
              <div className="wf-body">
                <div className="wf-phase">{s.phase}</div>
                <div className="wf-title">{s.title}</div>
                <div className="wf-desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BENEFITS ────────────────────────────────────────────────────────────────
const BENS = [
  { Icon: IC.ChartBar, n: 90, s: "%", l: "Less Manual Work", d: "Paperwork and spreadsheet cycles replaced by structured digital workflows." },
  { Icon: IC.Users, n: 3, s: "x", l: "Better Lead Conversion", d: "Timely follow-ups and counseling pipelines improve patient conversion significantly." },
  { Icon: IC.ClipBoard, n: 100, s: "%", l: "Centralized Records", d: "One system. Every patient. Every cycle. Every invoice. No data silos." },
  { Icon: IC.Activity, n: 0, s: "", l: "Missed Follow-Ups", d: "Automated reminders mean no patient or lead falls through the cracks." },
];

function Benefits() {
  return (
    <section id="benefits" className="sec benefits">
      <div className="inner">
        <div className="rv" style={{ textAlign: "center", marginBottom: "3rem" }}>
          <div className="label" style={{ justifyContent: "center" }}><span className="label-line" />Outcomes</div>
          <h2 className="sec-h">What Clinics Experience After Adoption</h2>
          <p className="sec-p" style={{ maxWidth: 500, margin: ".75rem auto 0" }}>
            Real improvements in operations, patient management, and revenue — not just software features.
          </p>
        </div>
        <div className="ben-grid rv">
          {BENS.map((b, i) => (
            <div className="ben-card" key={b.l} style={{ transitionDelay: `${i * 80}ms` }}>
              <div className="ben-ic"><b.Icon /></div>
              <div className="ben-n"><AnimCounter target={b.n} suffix={b.s} /></div>
              <div className="ben-l">{b.l}</div>
              <div className="ben-d">{b.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── WHY ─────────────────────────────────────────────────────────────────────
const WHY_PTS = [
  { Icon: IC.Heart, t: "Built from Real IVF Clinic Experience", d: "Our IVF software was shaped by working directly inside fertility clinics — understanding actual workflows, staff pain points, and clinical requirements." },
  { Icon: IC.Users, t: "Easy for Clinic Staff to Use", d: "No steep learning curve. The interface is designed for doctors, nurses, counselors, and billing staff — not IT professionals." },
  { Icon: IC.Activity, t: "Fully Customizable to Your Clinic", d: "Protocols, packages, forms, and reports are customized to match your clinic's exact way of working. Not a one-size-fits-all product." },
  { Icon: IC.ChartBar, t: "Scalable as Your Clinic Grows", d: "Start with core modules and add capabilities over time. Our IVF patient management software scales with your patient volume and team size." },
  { Icon: IC.Package, t: "Long-Term Support Included", d: "We don't ship software and disappear. We provide ongoing support, training, and feature updates — treating your clinic as a long-term partner." },
];

function WhyUs() {
  return (
    <section id="why-us" className="sec why">
      <div className="inner">
        <div className="why-inner">
          <div className="rv-l">
            <div className="label"><span className="label-line" />Why Choose Us</div>
            <h2 className="sec-h">Why Fertility Clinics<br />Choose Hypervolar</h2>
            <p className="sec-p" style={{ marginTop: ".75rem" }}>
              There are generic clinic management tools. Then there's IVF software built by people who understand fertility workflows. The difference is everything.
            </p>
            <ul className="why-points">
              {WHY_PTS.map((w) => (
                <li className="why-pt" key={w.t}>
                  <div className="wp-ic"><w.Icon /></div>
                  <div>
                    <div className="wp-t">{w.t}</div>
                    <div className="wp-d">{w.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="rv-r">
            <div className="why-quote">
              <div className="wq-ico"><IC.Quote /></div>
              <blockquote className="wq-text">
                "The IVF management system developed by Hypervolar transformed our clinic operations. From patient tracking to reporting, everything is now efficient and reliable."
              </blockquote>
              <div className="wq-author">
                <div className="wq-av">S</div>
                <div>
                  <div className="wq-name">Sukhwant Gandhi</div>
                  <div className="wq-co">Director, Aveya IVF Clinic</div>
                </div>
              </div>
            </div>
            <div style={{ marginTop: "1.25rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              {[
                { n: "5+", l: "Years", d: "Healthcare IT" },
                { n: "1", l: "Live IVF System", d: "in production" },
                { n: "20+", l: "Systems", d: "delivered" },
                { n: "24/7", l: "Support", d: "post-launch" },
              ].map((s) => (
                <div key={s.l} style={{ background: "var(--surf)", border: "1px solid var(--border)", borderRadius: "var(--r)", padding: "1.25rem", textAlign: "center", transition: "all .3s", cursor: "default" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--green-mid)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <div style={{ fontFamily: "var(--D)", fontSize: "2rem", fontWeight: 700, color: "var(--navy)", lineHeight: 1 }}>{s.n}</div>
                  <div style={{ fontSize: ".8rem", fontWeight: 600, color: "var(--green)", marginTop: ".35rem" }}>{s.l}</div>
                  <div style={{ fontSize: ".72rem", color: "var(--txt3)", marginTop: ".2rem" }}>{s.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: "What is IVF software?",
    a: "IVF software is a specialized clinic management system designed specifically for fertility and IVF clinics. Unlike generic hospital management systems, it includes modules purpose-built for fertility care — EMR with IVF-specific fields, stimulation cycle tracking, embryology lab records, counseling CRM, IVF package billing, and fertility drug inventory. It replaces spreadsheets, paper records, and disconnected tools with one integrated platform."
  },
  {
    q: "How does an IVF management system work?",
    a: "An IVF management system digitalizes the entire fertility treatment journey. From the first patient inquiry, the system captures leads and counseling records. Once treatment starts, it tracks stimulation protocols day by day, records egg retrieval and fertilization outcomes, manages the embryology lab, and documents the transfer. Post-procedure follow-ups and pregnancy outcomes are recorded. All billing, inventory, and reporting happen in the same platform — giving clinic staff a complete view of every patient and every cycle."
  },
  {
    q: "Is Hypervolar's IVF clinic software customizable?",
    a: "Yes, fully. We customize the system to match your clinic's specific stimulation protocols, treatment packages, report formats, billing structures, and clinical forms. We do not force your clinic into a fixed workflow — we adapt the software to how your team already operates, then improve from there."
  },
  {
    q: "Does it include billing and CRM?",
    a: "Yes. The IVF billing module handles complex package-based pricing — including bundled procedures, medicines, add-ons, and discounts — with itemized invoicing and payment tracking. The CRM module manages patient inquiries, counseling pipelines, follow-up reminders, and conversion tracking. Both are fully integrated with patient records and cycle data."
  },
  {
    q: "What is IVF cycle tracking software?",
    a: "IVF cycle tracking software is a module within the IVF management system that records and monitors every stage of a patient's IVF cycle — day-by-day stimulation medicine dosages, follicle scan measurements, hormone level logs, egg retrieval outcomes, embryo development stages and grading, transfer details, and final cycle outcomes. It provides a structured, chronological timeline for each cycle directly linked to the patient's record."
  },
  {
    q: "Has Hypervolar's IVF software been deployed at a real clinic?",
    a: "Yes. Our IVF management system is live and actively used at Aveya IVF clinic, managing daily patient records, cycle tracking, counseling workflows, billing, and operational reporting. This is not a prototype or demo environment — it is a production system handling real clinical operations."
  },
];

function FAQ() {
  const [open, setOpen] = useState(null);
  const toggle = (i) => setOpen(prev => prev === i ? null : i);
  return (
    <section id="faq" className="sec faq">
      <div className="inner">
        <div className="faq-inner">
          <div className="rv-l">
            <div className="label"><span className="label-line" />FAQ</div>
            <h2 className="sec-h">Frequently Asked Questions</h2>
            <p className="sec-p" style={{ marginTop: ".75rem", marginBottom: "2rem" }}>
              Everything clinics ask before adopting our IVF software — answered clearly.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
              {[
                { icon: IC.Mail, label: "Email", val: "hypervolar@gmail.com", href: "mailto:hypervolar@gmail.com" },
                { icon: IC.Phone, label: "Phone / WhatsApp", val: "+91 78274 36219", href: "tel:+917827436219" },
              ].map((c) => (
                <a key={c.label} href={c.href} style={{ display: "flex", alignItems: "center", gap: ".875rem", padding: ".9rem 1.1rem", background: "var(--surf2)", border: "1px solid var(--border)", borderRadius: "var(--r)", transition: "all .3s", textDecoration: "none" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--green-mid)"; e.currentTarget.style.transform = "translateX(6px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.transform = "translateX(0)"; }}>
                  <div style={{ width: 38, height: 38, borderRadius: 8, background: "var(--green-pale)", border: "1px solid var(--green-mid)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--green)", flexShrink: 0 }}>
                    <c.icon />
                  </div>
                  <div>
                    <div style={{ fontSize: ".67rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--txt3)" }}>{c.label}</div>
                    <div style={{ fontSize: ".88rem", fontWeight: 600, color: "var(--navy)", marginTop: 2 }}>{c.val}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
          <div className="rv-r">
            <div className="faq-list">
              {FAQS.map((f, i) => (
                <div className={`faq-item${open === i ? " open" : ""}`} key={i}>
                  <div className="faq-q" onClick={() => toggle(i)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && toggle(i)} aria-expanded={open === i}>
                    <span>{f.q}</span>
                    <span className="faq-arrow">▾</span>
                  </div>
                  <div className="faq-a">{f.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── CONTACT ─────────────────────────────────────────────────────────────────
function Contact() {
  const [form, setForm] = useState({ name: "", email: "", clinic: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("https://nexcrm.run.place/contact", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (res.ok) { setSent(true); setForm({ name: "", email: "", clinic: "", message: "" }); }
      else alert("Something went wrong. Please try again or WhatsApp us.");
    } catch { alert("Server error. Please WhatsApp us directly at +91 78274 36219"); }
    setLoading(false);
  };
  const wa = () => window.open("https://wa.me/917827436219?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20for%20the%20IVF%20software", "_blank");
  return (
    <section id="contact" className="sec" style={{ background: "var(--surf2)", borderTop: "1px solid var(--border)" }}>
      <div className="inner">
        <div style={{ textAlign: "center", marginBottom: "3rem" }} className="rv">
          <div className="label" style={{ justifyContent: "center" }}><span className="label-line" />Book a Demo</div>
          <h2 className="sec-h">Ready to Transform Your IVF Clinic?</h2>
          <p className="sec-p" style={{ maxWidth: 520, margin: ".75rem auto 0" }}>
            See the IVF management system live — with your real workflows in mind. Demos are free, no-commitment, and tailored to your clinic's needs.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem", maxWidth: 860, margin: "0 auto" }} className="rv">
          {/* Left info */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {[
              { t: "What you'll see in the demo", items: ["Live IVF cycle tracking in action", "Patient EMR and records module", "Billing and package management", "Counseling CRM and follow-ups", "Inventory and reporting dashboard"] },
            ].map((block) => (
              <div key={block.t} style={{ background: "var(--surf)", border: "1px solid var(--border)", borderRadius: "var(--r2)", padding: "1.75rem" }}>
                <div style={{ fontSize: ".85rem", fontWeight: 700, color: "var(--navy)", marginBottom: "1rem" }}>{block.t}</div>
                <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  {block.items.map((item) => (
                    <li key={item} style={{ display: "flex", alignItems: "center", gap: ".7rem", fontSize: ".83rem", color: "var(--txt2)" }}>
                      <span style={{ color: "var(--green)", flexShrink: 0 }}><IC.Check /></span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <button className="btn-wa" onClick={wa} style={{ justifyContent: "center" }}>
              <IC.WhatsApp /> WhatsApp for Instant Response
            </button>
          </div>
          {/* Form */}
          <div style={{ background: "var(--surf)", border: "1px solid var(--border)", borderRadius: "var(--r2)", padding: "2.5rem", boxShadow: "0 8px 40px rgba(0,0,0,.06)", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,var(--green),var(--navy))" }} />
            {!sent ? (
              <>
                <div style={{ fontFamily: "var(--D)", fontSize: "1.4rem", fontWeight: 700, color: "var(--navy)", marginBottom: ".35rem" }}>Book a Free Demo</div>
                <p style={{ fontSize: ".8rem", color: "var(--txt3)", marginBottom: "1.5rem" }}>We respond within 24 hours and schedule demos at your convenience.</p>
                <form onSubmit={handleSend} noValidate>
                  {[
                    { id: "dn", label: "Your Name", name: "name", type: "text", placeholder: "Dr. Priya Sharma", value: form.name },
                    { id: "de", label: "Email Address", name: "email", type: "email", placeholder: "you@clinic.in", value: form.email },
                    { id: "dc", label: "Clinic Name", name: "clinic", type: "text", placeholder: "Your fertility clinic name", value: form.clinic },
                  ].map((f) => (
                    <div key={f.id} style={{ marginBottom: "1rem" }}>
                      <label htmlFor={f.id} style={{ display: "block", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--txt2)", marginBottom: 7 }}>{f.label}</label>
                      <input id={f.id} name={f.name} type={f.type} value={f.value} onChange={handleChange} placeholder={f.placeholder} required
                        style={{ width: "100%", background: "var(--surf2)", border: "1px solid var(--border)", borderRadius: 8, padding: ".8rem 1rem", color: "var(--txt)", fontFamily: "var(--B)", fontSize: ".88rem", outline: "none", transition: "all .3s" }}
                        onFocus={e => { e.target.style.borderColor = "var(--green)"; e.target.style.background = "var(--surf)"; e.target.style.boxShadow = "0 0 0 3px var(--green-pale)"; }}
                        onBlur={e => { e.target.style.borderColor = "var(--border)"; e.target.style.background = "var(--surf2)"; e.target.style.boxShadow = "none"; }} />
                    </div>
                  ))}
                  <div style={{ marginBottom: "1rem" }}>
                    <label htmlFor="dm" style={{ display: "block", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--txt2)", marginBottom: 7 }}>Message (optional)</label>
                    <textarea id="dm" name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your clinic or specific requirements" rows={3}
                      style={{ width: "100%", background: "var(--surf2)", border: "1px solid var(--border)", borderRadius: 8, padding: ".8rem 1rem", color: "var(--txt)", fontFamily: "var(--B)", fontSize: ".88rem", outline: "none", resize: "none", transition: "all .3s" }}
                      onFocus={e => { e.target.style.borderColor = "var(--green)"; e.target.style.background = "var(--surf)"; }}
                      onBlur={e => { e.target.style.borderColor = "var(--border)"; e.target.style.background = "var(--surf2)"; }} />
                  </div>
                  <button type="submit" style={{ width: "100%", padding: ".9rem", border: "none", borderRadius: 8, background: "var(--navy)", color: "#fff", fontWeight: 600, fontFamily: "var(--B)", fontSize: ".9rem", cursor: "pointer", position: "relative", overflow: "hidden", letterSpacing: ".02em", transition: "all .3s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--green)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "var(--navy)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                    {loading ? "Sending…" : "Book Free Demo →"}
                  </button>
                </form>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
                <div style={{ color: "var(--green)", display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
                  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                </div>
                <div style={{ fontFamily: "var(--D)", fontSize: "1.6rem", fontWeight: 700, color: "var(--navy)", marginBottom: ".5rem" }}>Demo Request Sent!</div>
                <p style={{ color: "var(--txt3)", fontSize: ".88rem" }}>We'll be in touch within 24 hours to schedule your demo.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ───────────────────────────────────────────────────────────────
function FinalCTA() {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const wa = () => window.open("https://wa.me/917827436219?text=Hi%2C%20I%27d%20like%20to%20book%20a%20demo%20for%20the%20IVF%20software", "_blank");
  return (
    <section className="final-cta">
      <div className="cta-in rv">
        <div className="label" style={{ justifyContent: "center", color: "rgba(255,255,255,.45)" }}>
          <span style={{ width: 28, height: 1.5, background: "rgba(255,255,255,.3)", display: "inline-block" }} />
          Book a Demo for Your Clinic
        </div>
        <h2 className="sec-h">Stop Managing IVF on Spreadsheets.</h2>
        <p className="sec-p">
          Book a free demo of our <strong style={{ color: "rgba(255,255,255,.9)" }}>IVF clinic management system</strong> — see how it fits your actual workflow, ask every question, and make an informed decision. No pressure, no commitment.
        </p>
        <div className="cta-btns">
          <button className="btn-white" onClick={() => go("contact")}>Book a Free Demo →</button>
          <button className="btn-ow" onClick={wa}><IC.WhatsApp /> WhatsApp Us Now</button>
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ──────────────────────────────────────────────────────────────────
function Footer() {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <footer>
      <div className="foot-in">
        <div>
          <div className="foot-logo">HYPER<span>VOLAR</span></div>
          <div className="foot-tag">IVF Software · IVF Clinic Management · Healthcare IT · India</div>
        </div>
        <nav className="foot-links">
          {[
            { id: "features", label: "Features" },
            { id: "workflow", label: "IVF Workflow" },
            { id: "why-us", label: "Why Us" },
            { id: "faq", label: "FAQ" },
            { id: "contact", label: "Book Demo" },
          ].map(({ id, label }) => (
            <a href={`#${id}`} key={id} onClick={(e) => { e.preventDefault(); go(id); }}>{label}</a>
          ))}
          <a href="https://www.hypervolar.com">← Main Website</a>
        </nav>
        <div className="copy">© {new Date().getFullYear()} Hypervolar · IVF Software & Fertility Clinic Management System · India · All rights reserved</div>
      </div>
    </footer>
  );
}

// ─── ROOT ────────────────────────────────────────────────────────────────────
export default function IVFSoftwarePage() {
  useReveal();
  return (
    <>
      <SEOHead />
      <style>{CSS}</style>
      <FontLink />
      <Nav />
      <main>
        <Hero />
        <StatsStrip />
        <Problem />
        <Solution />
        <Features />
        <Workflow />
        <Benefits />
        <WhyUs />
        <FAQ />
        <Contact />
        <FinalCTA />
      </main>
      <Footer />
    </>
  );
}